/**
 * Pure rules of "สคริปต์แนะนำรายคน" (owner 17 ต.ค. 69 · api/script): reading the API's script safely, the order of
 * the suggestions (an apology is never first), the text a suggestion puts in the typing box, and the Thai lines of
 * the lean customer panel. No Vue here.
 */
import type {
  ConversationScript,
  PrefillAddress,
  RepeatOrderCart,
  ScriptCustomerReply,
  ScriptFact,
  ScriptPart,
  ScriptPackSuggest,
  ScriptProblem,
  ScriptReturning,
  ScriptSession,
  ScriptStep,
  ScriptStyleKey,
  ScriptSuggestion,
  ScriptUndefinedTopic,
  ScriptUpsell,
} from '../api/script/types'
import type { PosCartItem, PosCatalog, PosShipping } from '../api/pos/types'
import type { ContactTagRef, PosStats } from '../api/types'
import { baht, clockTime, dayKey, shortDate } from './inbox'
import { dayNotes, deliveryDateLabel, deliveryOf, isLocked, type PosSlotLike } from './order'
import { normalizeStyle } from './style'

const arr = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : [])

/** The API may leave a list out or add fields: lists default to empty, unknown fields are kept but never read. */
export function normalizeScript(raw: ConversationScript): ConversationScript {
  const s = raw as Partial<ConversationScript> & { conversation_id: string }
  return {
    ...s,
    conversation_id: String(s.conversation_id ?? ''),
    segment: s.segment === 'returning' ? 'returning' : 'new',
    computed_at: s.computed_at ?? '',
    stale: !!s.stale,
    enabled: s.enabled !== false,
    stage: s.stage ?? null,
    pending: arr(s.pending),
    known: arr(s.known),
    missing: arr(s.missing),
    steps: arr<ScriptStep>(s.steps),
    suggestions: arr<ScriptSuggestion>(s.suggestions).map((x) => ({ ...x, parts: arr(x.parts), why: arr(x.why) })),
    returning: s.returning ?? null,
    problems: arr(s.problems),
    undefined_topics: arr<ScriptUndefinedTopic>(s.undefined_topics).filter((u) => !!u && !!u.label),
    style: normalizeStyle(s.style),
    order_prefill: s.order_prefill && typeof s.order_prefill === 'object' ? s.order_prefill : null,
    experiment: s.experiment && (s.experiment.variant === 'A' || s.experiment.variant === 'B') ? s.experiment : null,
    playbook: s.playbook && typeof s.playbook === 'object' && typeof s.playbook.type === 'string' ? s.playbook : null,
    session: s.session && typeof s.session === 'object' && typeof s.session.since === 'string' ? s.session : null,
  }
}

// ------------------------------------------------ การสั่งครั้งนี้ (owner 18 ต.ค. 69 "ระวังเวลาลูกค้าเก่าสั่งซื้อใหม่
// ไม่ใช่ไปอ่านออเดอร์เก่ามานะ" · api/app/ai/script/session.py): the order tab is filled only from the purchase talked
// about now — after the chat's last saved order (done / held), the customer's newest POS order, a 3-day silence.

/** An order of the chat as the order tab's context lists it (PosContext.chat_orders). */
export interface ChatOrderLike {
  status: string
  created_at: string | null
}

const CLOSING = new Set(['done', 'held'])

/**
 * The script's prefill is about a purchase an order has closed since: an order saved from the chat (done / held) after
 * the script's boundary — the script was computed before it (the API recomputes it; until then it must not fill).
 */
export function prefillOutdated(s: ConversationScript | null | undefined, orders: ChatOrderLike[] | null | undefined): boolean {
  if (!s) return false
  const since = s.session?.since ? Date.parse(s.session.since) : -Infinity
  return (orders ?? []).some((o) => CLOSING.has(o.status) && !!o.created_at && Date.parse(o.created_at) > since)
}

/** The AI-mode session as GET …/ai-mode gives it (api/aimode/types.ts AiSession) — every field optional (older API). */
export interface AiSessionLike {
  open?: boolean
  started_at?: string | null
  ended_at?: string | null
  end_reason?: string | null
  order_id?: string | null
}

/**
 * May the order tab take the AI session's cart (stores/script.ts aiCart)? Never one whose order was placed (it would
 * be ordered twice), nor one that started before this purchase's boundary (its cart is an earlier order's) — except an
 * open session after a silence: the AI carries the chat on. No boundary (a first-time customer): as before.
 */
export function aiSessionUsable(a: AiSessionLike | null | undefined, purchase: ScriptSession | null | undefined): boolean {
  if (!a) return false
  if (a.order_id || a.end_reason === 'order_done') return false
  if (!purchase?.since) return true
  const started = a.started_at ? Date.parse(a.started_at) : NaN
  if (!Number.isNaN(started) && started > Date.parse(purchase.since)) return true
  return a.open === true && !a.ended_at && purchase.reason === 'silence'
}

/** What the order tab takes from an AI session's state (api/app/ai/sales/state.py): the cart the AI agreed with the
 *  customer. `setMenuId` null = the pack is not sold any more (or none); `date` null = no day, or one the POS no longer
 *  sells (a note says why — the staff pick the day). */
export interface AiCartPatch {
  setMenuId: number | null
  packName: string | null
  items: PosCartItem[]
  shipping: PosShipping | null
  date: string | null
  time: string | null
  notes: string[]
}

const DAY_MS = 86400000

/**
 * Owner 18 ต.ค. 69 "ทำไมไม่เลือกเมนูให้ … เวลาด้วย": the AI session's pack, dishes, carrier, delivery day and slot. The day
 * only while the POS still sells it — inside the calendar's range, not a locked Lalamove day, not a day off of that
 * carrier, before the chat cut-off of the day before (`shipping.open_until` from the AI, else 22:00 Bangkok). null = the
 * session has neither a pack nor dishes.
 */
export function aiCartPatch(st: Record<string, unknown> | null | undefined, catalog: PosCatalog | null,
                            nowMs: number = Date.now()): AiCartPatch | null {
  const pack = st?.package as { set_menu_id?: unknown; name?: unknown } | null | undefined
  const packId = typeof pack?.set_menu_id === 'number' ? pack.set_menu_id : null
  const items = (Array.isArray(st?.items) ? st.items : [])
    .map((i) => i as { menu_id?: unknown; qty?: unknown })
    .filter((i): i is { menu_id: number; qty: number } => typeof i.menu_id === 'number' && typeof i.qty === 'number' && i.qty > 0)
    .map((i) => ({ menu_id: i.menu_id, qty: i.qty }))
  if (!packId && !items.length) return null
  const sold = packId !== null && !!catalog?.set_menus.some((m) => m.id === packId)
  const ship = st?.shipping as { method?: unknown; time?: unknown; date?: unknown; open_until?: unknown } | null | undefined
  const shipping: PosShipping | null = ship?.method === 'lalamove' || ship?.method === 'dhl' ? ship.method : null
  const d = deliveryOf(catalog, nowMs)
  // the slot the AI agreed (a POS slot key) — only one the POS still has
  const time = shipping === 'lalamove' && typeof ship?.time === 'string' && d.slots.some((x) => x.time === ship.time) ? ship.time : null
  const notes: string[] = []
  const day = typeof ship?.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(ship.date) ? ship.date : null
  let date: string | null = null
  if (day && shipping) {
    const open = typeof ship?.open_until === 'string' ? Date.parse(ship.open_until) : NaN
    const until = Number.isNaN(open) ? Date.parse(`${day}T22:00:00+07:00`) - DAY_MS : open
    const off = dayNotes(d, day, shipping)
    let why: string | null = null
    if (nowMs >= until) why = 'ส่งไม่ทันแล้ว (เลยเวลาปิดรับของวันก่อนวันส่ง)'
    else if (day < d.date_min || day > d.date_max) why = 'อยู่นอกช่วงวันที่ POS รับ'
    else if (isLocked(d, day, shipping)) why = 'ปิดรับแล้ว (ทีมยิงงาน Lalamove ของวันนั้นไปแล้ว)'
    else if (off.length) why = `ไม่มีรอบส่ง (${off[0]})`
    if (why) notes.push(`วันส่งที่ AI คุยกับลูกค้า ${deliveryDateLabel(day)} ${why} · เลือกวันส่งเอง`)
    else date = day
  }
  return {
    setMenuId: sold ? packId : null,
    packName: typeof pack?.name === 'string' ? pack.name : null,
    items,
    shipping,
    date,
    time,
    notes,
  }
}

/**
 * คู่มือปิดการขาย, the panel's note on the step now (owner 18 ต.ค. 69 — instead of "ถามไปแล้ว รอลูกค้าตอบ" when the
 * customer replied): "ลูกค้าถามกลับ: “มีแพคอื่นอีกไหม” · ตอบตามคู่มือ: ถามตัวเลือก" · an objection "ลูกค้าตอบกลับ: …" ·
 * nothing in the playbook "… · ยังไม่มีในคู่มือ ให้ AI ช่วยพาไปขั้นต่อไป".
 */
/** The title of "ให้ AI พาไปขั้นต่อไป" (the suggestion card and the step card). */
export const NEXT_STEP_TITLE =
  'ให้ AI เขียน 1 ข้อความ: ตอบสิ่งที่ลูกค้าพูด แล้วถามต่อไม่เกิน 1 คำถามตามขั้นตอนการขาย ใช้ข้อมูลจริงของร้านเท่านั้น (ใช้ค่า AI ต่อครั้ง)'

export function replyNote(r: ScriptCustomerReply): { lead: string; quote: string; how: string } {
  return {
    lead: r.question ? 'ลูกค้าถามกลับ' : 'ลูกค้าตอบกลับ',
    quote: `“${r.text}”`,
    how: r.in_playbook ? `ตอบตามคู่มือ: ${r.label}` : 'ยังไม่มีในคู่มือ ให้ AI ช่วยพาไปขั้นต่อไป',
  }
}

/** "ยังไม่ได้กำหนดคำตอบเรื่อง ราคา, ค่าส่ง" — the answer types the owner has not switched an answer on for (null = none). */
export function undefinedLine(list: ScriptUndefinedTopic[]): string | null {
  const labels = [...new Set(list.map((u) => u.label))]
  return labels.length ? `ยังไม่ได้กำหนดคำตอบเรื่อง ${labels.join(', ')}` : null
}

/**
 * Owner 17 ต.ค. 69: "an apology for a past problem may be offered but never as the first suggestion". The API sends
 * the recommended one first; the UI also keeps every apology behind the others. Only apologies = nothing to show
 * (there is no first suggestion an apology could be the alternative to).
 */
export function orderSuggestions(list: ScriptSuggestion[]): ScriptSuggestion[] {
  const main = list.filter((s) => s.kind !== 'apology')
  if (!main.length) return []
  return [...main, ...list.filter((s) => s.kind === 'apology')]
}

/** The parts to show; a suggestion without parts is one plain text. */
export function partsOf(s: ScriptSuggestion): ScriptPart[] {
  return s.parts.length ? s.parts : [{ t: 'text', text: s.text }]
}

/** "[ค่าส่ง]" — how a blank goes into the typing box (and what blocks sending until the staff fill it). */
export const gapToken = (label: string): string => `[${label}]`

/** What "ใส่ในช่องพิมพ์" puts in the typing box: the message, blanks written as "[label]". */
export function suggestionText(s: ScriptSuggestion): string {
  if (!s.parts.length) return s.text
  return s.parts.map((p) => (p.t === 'gap' ? gapToken(p.label) : p.text)).join('')
}

/** The blanks of a suggestion, as they appear in the typing box. */
export function gapTokens(s: ScriptSuggestion): string[] {
  return [...new Set(s.parts.filter((p): p is { t: 'gap'; label: string } => p.t === 'gap').map((p) => gapToken(p.label)))]
}

/** The customer styles (api/app/ai/style_adapt.py LABEL): used when the API sends a style without its label. */
export const STYLE_LABEL: Record<ScriptStyleKey, string> = {
  polite_short: 'สุภาพ กระชับ',
  polite_detailed: 'สุภาพ ละเอียด',
  casual: 'เป็นกันเอง',
  curt: 'ห้วน ตอบสั้น',
  english: 'ภาษาอังกฤษ',
}

export interface AdaptStyle {
  key: ScriptStyleKey
  label: string
  returning: boolean
  /** false = the script has no style (yet): the API re-tones for สุภาพ กระชับ. */
  known: boolean
}

/** The style "ปรับตามแนวลูกค้า" re-tones for: the script's, or สุภาพ กระชับ when it has none (as the API does). */
export function adaptStyle(s: ConversationScript | null | undefined): AdaptStyle {
  const st = s?.style
  if (!st || !(st.key in STYLE_LABEL)) return { key: 'polite_short', label: STYLE_LABEL.polite_short, returning: false, known: false }
  return { key: st.key, label: (st.label ?? '').trim() || STYLE_LABEL[st.key], returning: st.returning === true, known: true }
}

/** "ห้วน ตอบสั้น" · "เป็นกันเอง · ลูกค้าเก่า" — the style as the button / the draft box name it. */
export function styleText(st: { label: string; returning: boolean }): string {
  return st.returning && !st.label.includes('ลูกค้าเก่า') ? `${st.label} · ลูกค้าเก่า` : st.label
}

/** "This message" for ข้าม / used: the newest unanswered customer message (or the script itself when none). */
export function messageKey(s: ConversationScript | null): string {
  if (!s) return ''
  const last = s.pending[s.pending.length - 1]
  return last ? `m:${last.message_id}` : `t:${s.computed_at}`
}

const SOURCE: Record<string, string> = {
  chat: 'แชทนี้',
  pos: 'POS',
  web: 'หน้าเว็บ',
  stats: 'สถิติแชท LINE ของร้าน',
  shipping: 'ขนส่ง',
  carrier: 'ขนส่ง',
  menu: 'เมนูรอบนี้',
  rules: 'กฎของร้าน',
}
export const sourceLabel = (src: string | null | undefined): string | null => (src ? (SOURCE[src] ?? src) : null)

const KIND_LABEL: Record<string, string> = {
  answer: 'ตอบคำถาม',
  opener: 'ทักทาย',
  apology: 'ขอโทษ',
  upsell: 'ชวนเพิ่มแพ็ก',
  ask: 'ถามข้อมูล',
  followup: 'ทักตาม',
}
export const kindLabel = (k: string): string => KIND_LABEL[k] ?? 'ทางเลือก'

// ------------------------------------------------ panel: header

/** "วันนี้" · "เมื่อวาน" · "3 วันก่อน" (Bangkok days). */
export function daysAgoText(iso: string | null, nowMs: number): string | null {
  if (!iso || Number.isNaN(Date.parse(iso))) return null
  const d = Math.round((Date.parse(`${dayKey(nowMs)}T12:00:00+07:00`) - Date.parse(`${dayKey(iso)}T12:00:00+07:00`)) / 86400000)
  if (d <= 0) return 'วันนี้'
  if (d === 1) return 'เมื่อวาน'
  return `${d} วันก่อน`
}

/** "ทักครั้งแรกวันนี้ 14:02" · "ทักครั้งแรก 12 ต.ค." */
export function firstSeenText(iso: string | null | undefined, nowMs: number): string | null {
  if (!iso || Number.isNaN(Date.parse(iso))) return null
  return dayKey(iso) === dayKey(nowMs) ? `ทักครั้งแรกวันนี้ ${clockTime(iso)}` : `ทักครั้งแรก ${shortDate(iso)}`
}

export interface StatsParts {
  orders: number
  total: number | null
  lastAt: string | null
  usualGap: number | null
  overdue: number | null
}

/** The returning customer's numbers: the script first (POS through the API), else the detail's POS block. */
export function statsOf(r: ScriptReturning | null | undefined, pos: PosStats | null | undefined): StatsParts | null {
  if (r && r.orders > 0) {
    return { orders: r.orders, total: r.total, lastAt: r.last_at, usualGap: r.usual_gap_days, overdue: r.overdue_days }
  }
  if (pos && pos.orders_count > 0) {
    return { orders: pos.orders_count, total: pos.total_spent, lastAt: pos.last_order_at, usualGap: pos.usual_interval_days, overdue: null }
  }
  return null
}

/** "สั่ง 6 ครั้ง · ยอดรวม ฿12,340 · ล่าสุด 3 วันก่อน" (parts without data are left out). */
export function statsLine(p: StatsParts, nowMs: number): string {
  const last = daysAgoText(p.lastAt, nowMs)
  return [
    `สั่ง ${p.orders.toLocaleString('th-TH')} ครั้ง`,
    p.total !== null ? `ยอดรวม ${baht(p.total)}` : null,
    last ? `ล่าสุด${last === 'วันนี้' || last === 'เมื่อวาน' ? '' : ' '}${last}` : null,
  ]
    .filter(Boolean)
    .join(' · ')
}

/** The overdue chip: past their usual gap. `overdue_days` from the API; else from the last order and the usual gap. */
export function overdueChip(p: StatsParts, nowMs: number): { text: string; title: string | null } | null {
  let days = p.overdue
  if (days === null && p.usualGap !== null && p.lastAt) {
    const since = Math.floor((nowMs - Date.parse(p.lastAt)) / 86400000)
    days = since - p.usualGap
  }
  if (days === null || days <= 0) return null
  return { text: `เลยรอบปกติ ${days} วัน`, title: p.usualGap !== null ? `ปกติสั่งทุก ~${p.usualGap} วัน` : null }
}

const ALLERGY_KEYS = new Set(['allergy', 'allergies', 'allergen'])
const PREF_KEYS = new Set(['preference', 'preferences', 'spice', 'spicy', 'diet', 'dislike'])

export interface HeaderChip {
  text: string
  tone: 'crit' | 'mut'
  title: string | null
}

/** Allergy (red) and preference (grey) chips of the panel header: POS allergies + what the script knows. */
export function headerChips(known: ScriptFact[], posAllergies: string[] | null | undefined): HeaderChip[] {
  const out: HeaderChip[] = []
  const seen = new Set<string>()
  const add = (c: HeaderChip) => {
    const k = c.text.replace(/\s+/g, '')
    if (seen.has(k)) return
    seen.add(k)
    out.push(c)
  }
  for (const a of posAllergies ?? []) add({ text: a.startsWith('แพ้') ? a : `แพ้${a}`, tone: 'crit', title: 'จาก POS' })
  for (const f of known) {
    const v = f.value.trim()
    if (!v) continue
    if (ALLERGY_KEYS.has(f.key)) add({ text: v.startsWith('แพ้') ? v : `แพ้${v}`, tone: 'crit', title: `${f.label} · ${sourceLabel(f.source)}` })
    else if (PREF_KEYS.has(f.key)) add({ text: v, tone: 'mut', title: `${f.label} · ${sourceLabel(f.source)}` })
  }
  return out
}

/** Facts shown as chips in "ขายต่อ" (the header already shows allergies / preferences). */
export const sellFacts = (known: ScriptFact[]): ScriptFact[] => known.filter((f) => !ALLERGY_KEYS.has(f.key) && !PREF_KEYS.has(f.key))

// ------------------------------------------------ panel: ขายต่อ

/** "เสนอ แพ็ก 15 วัน · 45 มื้อ · มื้อละถูกลง ฿6" (the parts the API knows). */
export function upsellOffer(u: ScriptUpsell): { to: string; extra: string | null } {
  const bits: string[] = []
  if (u.to.meals) bits.push(`${u.to.meals} มื้อ`)
  const a = u.from.per_meal
  const b = u.to.per_meal
  if (typeof a === 'number' && typeof b === 'number' && a > b) bits.push(`มื้อละถูกลง ${baht(Math.round((a - b) * 100) / 100)}`)
  else if (typeof b === 'number') bits.push(`มื้อละ ${baht(b)}`)
  return { to: u.to.name, extra: bits.length ? bits.join(' · ') : null }
}

export const UPSELL_REASON: Record<string, string> = {
  ran_out_fast: 'แพ็กล่าสุดหมดเร็วกว่าปกติ',
  same_pack_repeated: 'สั่งแพ็กเดิมซ้ำ',
}

// ------------------------------------------------ panel: ขั้นตอนการขาย (the checklist in ขายต่อ)

/** auto · custom · system — an older body without `kind`: by its key. */
export function stepKind(s: ScriptStep): 'auto' | 'custom' | 'system' {
  if (s.kind) return s.kind
  if (s.key === 'summary' || s.key === 'pay') return 'system'
  return s.key.startsWith('custom:') ? 'custom' : 'auto'
}

/**
 * Statuses from what is done (the API's rule, api/app/ai/script/steps.py `assign`): the first question step not done is
 * current, the later ones todo · the system steps are greyed ("system") until every question step is done.
 */
export function stepStatuses(steps: ScriptStep[], isDone: (s: ScriptStep) => boolean): ScriptStep[] {
  const done = new Map(steps.map((s) => [s.key, isDone(s)]))
  const out = steps.map((s) => ({ ...s }))
  const asks = out.filter((s) => stepKind(s) !== 'system')
  const sys = out.filter((s) => stepKind(s) === 'system')
  const cur = asks.find((s) => !done.get(s.key)) ?? null
  for (const s of asks) s.status = done.get(s.key) ? 'done' : s === cur ? 'current' : 'todo'
  const first = cur ? null : (sys.find((s) => !done.get(s.key)) ?? null)
  for (const s of sys) s.status = done.get(s.key) ? 'done' : s === first ? 'current' : 'system'
  return out
}

/** A custom step ticked / unticked, the rest as they are (shown at once while the API saves it). */
export const withTick = (steps: ScriptStep[], key: string, done: boolean): ScriptStep[] =>
  stepStatuses(steps, (s) => (s.key === key ? done : s.status === 'done'))

/** "2/4": the current question step's number, or every one when all are done (the system steps are not counted). */
export function stepsProgress(steps: ScriptStep[]): { n: number; of: number } {
  const asks = steps.filter((s) => stepKind(s) !== 'system')
  const i = asks.findIndex((s) => s.status === 'current')
  return { n: i >= 0 ? i + 1 : asks.filter((s) => s.status === 'done').length, of: asks.length }
}

// ------------------------------------------------ panel: ปัญหา / ติดตาม

/** Auto tags that are a problem to solve (the website said the payment failed / ran out of time). */
const PAY_TAGS = new Set(['webchat:pay_failed', 'webchat:pay_expired', 'webchat:ask:payment_failed'])
export const isProblemTag = (t: ContactTagRef): boolean => !!t.auto && PAY_TAGS.has(t.auto)

export const PROBLEM_LABEL: Record<string, string> = {
  delivery_late: 'ส่งช้า',
  delivery_failed: 'ส่งไม่สำเร็จ',
  payment_failed: 'ชำระเงินไม่ผ่าน',
  web_error: 'หน้าเว็บแจ้งปัญหา',
  claim: 'แจ้งปัญหาสินค้า',
}

/** The script's problems minus "ชำระเงินไม่ผ่าน" when the payment tag already says it (one line, not two). */
export function problemsShown(list: ScriptProblem[], tags: ContactTagRef[] | null | undefined): ScriptProblem[] {
  const payTag = (tags ?? []).some(isProblemTag)
  return list.filter((p) => !(payTag && p.kind === 'payment_failed'))
}

// ------------------------------------------------ "สั่งชุดเดิมซ้ำ" → order tab

export interface RepeatPatch {
  setMenuId: number | null
  /** The cart named a pack the catalog does not sell any more. */
  packMissing: boolean
  items: PosCartItem[] | null
  shipping: PosShipping | null
  label: string
  /** The summary handoff (order_prefill): the start day when it can still be delivered (null = keep the form's day). */
  date: string | null
  /** หมายเหตุถึงครัว (the foods to avoid) — null = leave the form's. */
  remark: string | null
  /** A new POS customer's address from the chat. */
  address: PrefillAddress | null
  /** The Lalamove slot (`time` key) the customer asked for — null = keep the form's. */
  time: string | null
  /** Lines for the staff above the form (the API's + why the requested day is not set). */
  notes: string[]
  /** การสั่งครั้งนี้: "อ่านเฉพาะแชทตั้งแต่ … (ออเดอร์ก่อนหน้า …)" — also the first of `notes`; null = the whole chat. */
  sessionNote: string | null
}

const norm = (s: string) => s.replace(/\s+/g, ' ').trim()

/** Reads the repeat-order cart in any of the shapes the order tab knows (see RepeatOrderCart). */
export function repeatPatch(cart: RepeatOrderCart, catalog: PosCatalog | null, nowMs: number = Date.now()): RepeatPatch {
  const byId = cart.set_menu_id ?? cart.setMenuId ?? null
  const name = cart.set_menu_name ?? cart.package ?? null
  let setMenuId: number | null = null
  if (typeof byId === 'number' && (!catalog || catalog.set_menus.some((s) => s.id === byId))) setMenuId = byId
  else if (name && catalog) setMenuId = catalog.set_menus.find((s) => norm(s.name) === norm(name))?.id ?? null
  const items = arr<{ menu_id: unknown; qty: unknown }>(cart.items)
    .map((i) => ({ menu_id: Number(i.menu_id), qty: Math.max(0, Math.round(Number(i.qty))) }))
    .filter((i) => Number.isFinite(i.menu_id) && i.qty > 0)
    .filter((i) => !catalog || catalog.menus.some((m) => m.id === i.menu_id))
  const ship = (cart.shipping ?? cart.carrier ?? '').toLowerCase()
  const shipping: PosShipping | null = ship === 'dhl' ? 'dhl' : ship === 'lalamove' ? 'lalamove' : null
  const src = cart.source
  const srcLabel = src && typeof src === 'object' ? src.label?.trim() || null : null
  const from = cart.order_code ? `ออเดอร์ ${cart.order_code}` : typeof src === 'string' ? src.replace(/^จาก/, '').trim() || null : null
  const label = cart.label?.trim() || srcLabel || (from ? `สั่งซ้ำจาก${from}` : 'สั่งชุดเดิมซ้ำ')
  const sessionNote = typeof cart.session_note === 'string' && cart.session_note.trim() ? cart.session_note.trim() : null
  const notes = [
    ...(sessionNote ? [sessionNote] : []),
    ...arr<unknown>(cart.notes).filter((n): n is string => typeof n === 'string' && !!n.trim()),
  ]
  const date = deliverableDate(cart, catalog, shipping, nowMs, notes)
  // the day refused when pressed: the label no longer names it ("เติมจากแชท · แพ็ก … · จ. 19 ต.ค.")
  const dayLabel = cart.date_label?.trim()
  const shown = cart.date && !date && dayLabel ? label.replace(` · ${dayLabel}`, '') : label
  const remark = typeof cart.remark === 'string' && cart.remark.trim() ? cart.remark.trim().slice(0, 255) : null
  const address = cart.address && typeof cart.address === 'object' ? cart.address : null
  const hour = typeof cart.time_hour === 'number' ? cart.time_hour : null
  const slots = deliveryOf(catalog, nowMs).slots
  // a slot key the chat settled on ("เติมจากแชททั้งหมด") — only while the POS still has that slot
  const key = typeof cart.time === 'string' && shipping !== 'dhl' && slots.some((s) => s.time === cart.time) ? cart.time : null
  const time = key ?? (hour !== null && shipping !== 'dhl' ? slotForHour(slots, hour) : null)
  if (hour !== null && time === null && shipping !== 'dhl') notes.push(`ลูกค้าขอเวลา: ${cart.time_wanted ?? `${hour}:00`} · ไม่มีรอบที่ตรง เลือกรอบเอง`)
  return {
    setMenuId,
    packMissing: setMenuId === null && (byId !== null || !!name),
    items: items.length ? items : null,
    shipping,
    label: shown,
    date,
    remark,
    address,
    time,
    notes,
    sessionNote,
  }
}

/** The slot an hour falls in ("10:00 - 12:00" holds 10 and 11; 12 → the slot ending at 12), else the nearest one that
 *  starts after it within 2 hours (owner 18 ต.ค. 69 "เลือกรอบให้ด้วยสิ"). null = no slot fits. */
export function slotForHour(slots: PosSlotLike[], hour: number): string | null {
  const span = (s: PosSlotLike): [number, number] | null => {
    const m = /(\d{1,2})[:.](\d{2})\s*[-–]\s*(\d{1,2})[:.](\d{2})/.exec(s.label)
    return m ? [Number(m[1]) + Number(m[2]) / 60, Number(m[3]) + Number(m[4]) / 60] : null
  }
  const withSpan = slots.map((s) => [s, span(s)] as const).filter((x): x is readonly [PosSlotLike, [number, number]] => !!x[1])
  const inside = withSpan.find(([, [a, b]]) => hour >= a && hour < b) ?? withSpan.find(([, [, b]]) => hour === b)
  if (inside) return inside[0].time
  const next = withSpan.filter(([, [a]]) => a > hour && a - hour <= 2).sort((x, y) => x[1][0] - y[1][0])[0]
  return next ? next[0].time : null
}

/**
 * The prefill's start day, checked again when the button is pressed (the script may be minutes old): inside the POS
 * calendar's range, not a locked Lalamove day, before the chat cut-off of the day before. Not deliverable → null (the
 * form keeps its day) and a note says why, like the API's `date_problem`.
 */
function deliverableDate(cart: RepeatOrderCart, catalog: PosCatalog | null, shipping: PosShipping | null, nowMs: number, notes: string[]): string | null {
  const day = typeof cart.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(cart.date) ? cart.date : null
  if (!day) return null
  const d = deliveryOf(catalog, nowMs)
  const label = cart.date_label?.trim() || day
  const until = cart.date_open_until ? Date.parse(cart.date_open_until) : NaN
  let why: string | null = null
  if (!Number.isNaN(until) && nowMs >= until) why = 'ส่งไม่ทันแล้ว (เลยเวลาปิดรับของวันก่อนวันส่ง)'
  else if (day < d.date_min || day > d.date_max) why = 'อยู่นอกช่วงวันที่ POS รับ'
  else if (isLocked(d, day, shipping ?? 'lalamove')) why = 'ปิดรับแล้ว (ทีมยิงงาน Lalamove ของวันนั้นไปแล้ว)'
  if (!why) return day
  notes.push(`วันที่ลูกค้าขอ: ${label} ${why} · เลือกวันส่งเอง`)
  return null
}

/** The summary row's line under "เปิดลงออเดอร์ (เติมให้แล้ว)": "แพ็ก 7 วัน 21 มื้อ (แนะนำ) · จ. 19 ต.ค. · แพ้กุ้ง". */
export function prefillSummary(p: RepeatOrderCart): string | null {
  const pack = (p.pack_label ?? '').trim()
  const day = (p.date_label ?? '').trim()
  const bits = [
    pack ? (p.pack_suggested ? `${pack} (แนะนำ)` : pack) : null,
    day ? (p.date ? day : `วันที่ขอ ${day} ส่งไม่ได้`) : null,
    (p.remark ?? '').trim() || null,
  ].filter(Boolean)
  return bits.length ? bits.join(' · ') : null
}

/** The pack row's recommendation: "แพ็ก 7 วัน 21 มื้อ" · "฿1,050 · มื้อละ ฿50" · "วันละ 2 มื้อ ทานได้ประมาณ 10 วัน". */
export function packSuggestLine(sg: ScriptPackSuggest): { name: string; price: string | null; why: string | null } {
  const price = [sg.price !== null && sg.price !== undefined ? baht(sg.price) : null, sg.per_meal ? `มื้อละ ${baht(sg.per_meal)}` : null]
    .filter(Boolean)
    .join(' · ')
  const basis = (sg.basis ?? '').trim()
  const why = basis ? (sg.lasts_days ? `${basis} ทานได้ประมาณ ${sg.lasts_days} วัน` : `จาก${basis}`) : null
  return { name: sg.name, price: price || null, why }
}
